import React, { useState } from 'react';
import { useLocalStorage } from 'react-use';
import { L } from '../../../../lib/i18n';
import styled from '@emotion/styled';
import CardWithDivider from '../../../../components/Layout/CardWithDivider';

import CardTitle from '../../../../components/typography/CardTitle';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import ContentNewsApi from '@app/api/public/ContentNewsApi';
import toast from 'react-hot-toast';
import Label from '../../../../components/Forms/Label';
import ImageDropZone from '../../../../components/ImageDropZone/ImageDropZone';
import Input from '../../../../components/Forms/Input';
import { formatDateTimeZone, formatInputDateTimeDayjs } from '@app/shared/lib/formatting';
import { style } from '../../../../components/Shared/Style';
import { Button } from '../../../../components/Buttons/Button';
import triggerDirtyFieldsToats from '../../../../components/Forms/DirtyFieldsToast';
import Quill from '../../../../components/Quill/Quill';
import { memberNewsQueryKeys } from '../hooks/memberNewsQueries';
import DropdownButton from '../../../../components/Buttons/DropdownButton';

const StyledCardWithDivider = styled(CardWithDivider)({
    width: 'fit-content',
});

const GridContainer = styled.div({
    display: 'grid',
    gap: '1rem',
    columnGap: '2rem',
    backgroundColor: style.colors.white,
    alignItems: 'start',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridTemplateAreas: `
    "img          img               img"
    "from         to                ..."
    "title        title             title"
    "description  description description"
    "reply_mail  reply_mail reply_mail"

    "buttons      buttons         buttons"
    `,
    paddingBottom: '0px',
    maxWidth: '600px',
});

const StyledImageDropZone = styled(ImageDropZone)({
    aspectRatio: '4/3',
});

const newsFormSchema = z.object({
    title: z.string().min(1, { message: L('required') }),
    description: z.string().min(1, { message: L('required') }),
    reply_mail: z.string().email(L('not_valid_email')).or(z.literal('')),
    published_from: z.string().optional(),
    published_to: z.string().optional(),
});

type CreateNews = { news: z.infer<typeof newsFormSchema>; image?: File };

const createNewsDraft = (variables: CreateNews) => {
    return ContentNewsApi.createDraft(variables);
};

type CreateAndPublish = {
    news: CreateNews['news'] & { published_from: string; published_to: string };
    image?: File;
};
const createNewNews = (variables: CreateAndPublish) => {
    return ContentNewsApi.createNewsAndPublish(variables);
};

const CreateNewNewsPage = () => {
    const history = useHistory();
    const [localSubmitButtonState, setSubmitButtonState] = useLocalStorage<'save_to_draft' | 'save_and_publish'>(
        'createNewNews',
        'save_to_draft'
    );
    const submitButtonState = localSubmitButtonState || 'save_to_draft';

    const quertyClient = useQueryClient();
    const { mutate: createDraft } = useMutation((variables: CreateNews) => createNewsDraft(variables), {
        onSettled: () => {
            quertyClient.invalidateQueries(memberNewsQueryKeys.lists());
        },
    });
    const { mutate: createAndPublishNews } = useMutation((variables: CreateAndPublish) => createNewNews(variables), {
        onSettled: () => {
            quertyClient.invalidateQueries(memberNewsQueryKeys.lists());
        },
    });

    const [imgFile, setImgFile] = useState<File>();
    const [imgUrl, setImgUrl] = useState<string>('');

    const onUpload = (files: File[]) => {
        setImgFile(files[0]);
        setImgUrl(URL.createObjectURL(files[0]));
    };

    const {
        register,
        handleSubmit,
        control,
        setError,
        getValues,
        formState: { errors, isDirty },
    } = useForm({
        resolver: zodResolver(newsFormSchema),
        defaultValues: {
            title: '',
            description: '',
            reply_mail: '',
            published_from: '',
            published_to: '',
        },
    });

    const submit = (data: z.infer<typeof newsFormSchema>, type: 'publish' | 'draft') => {
        if (type === 'publish') {
            if (!data.published_from)
                return setError('published_from', { message: L('required'), type: 'required' }, { shouldFocus: true });
            return createAndPublishNews(
                {
                    news: {
                        ...data,
                        published_from: formatDateTimeZone(data.published_from),
                        published_to: formatDateTimeZone(data.published_to),
                    },
                    image: imgFile,
                },
                {
                    onSuccess: (data) => {
                        toast.success(L('news_created_and_published'));
                        if (data?.id) {
                            history.push(`/news/${data.id}`);
                        }
                    },
                }
            );
        }

        createDraft(
            {
                news: {
                    ...data,
                    published_from: formatDateTimeZone(data.published_from),
                    published_to: formatDateTimeZone(data.published_to),
                },
                image: imgFile,
            },
            {
                onSuccess: (data) => {
                    toast.success(L('news_saved_as_draft'));
                    if (data?.id) {
                        history.push(`/news/${data.id}`);
                    }
                },
            }
        );
    };

    return (
        <StyledCardWithDivider
            topArea={<CardTitle>{L('news_new')}</CardTitle>}
            mainArea={
                <GridContainer>
                    <div style={{ gridArea: 'img' }}>
                        <Label title={L('image')} />
                        <StyledImageDropZone
                            dropzoneOptions={{}}
                            onDrop={onUpload}
                            url={imgUrl || ''}
                            remove={() => {
                                if (imgUrl || imgFile) {
                                    setImgUrl('');
                                    setImgFile(undefined);
                                }
                            }}
                        />
                    </div>

                    <Label
                        style={{ gridArea: 'from' }}
                        htmlFor="published_from"
                        title={`${L('publish_start')}*`}
                        error={errors?.published_from?.message}
                    >
                        <Input
                            type="datetime-local"
                            {...register('published_from', {
                                min: formatInputDateTimeDayjs(),
                            })}
                        />
                    </Label>

                    <Label
                        style={{ gridArea: 'to' }}
                        htmlFor="published_to"
                        title={L('publish_end')}
                        error={errors?.published_to?.message}
                    >
                        <Input
                            type="datetime-local"
                            {...register('published_to', {
                                min: formatInputDateTimeDayjs(),
                                disabled: !getValues('published_from').length,
                            })}
                        />
                    </Label>

                    <Label
                        style={{ gridArea: 'title' }}
                        htmlFor="title"
                        title={`${L('title')}*`}
                        error={errors?.title?.message}
                    >
                        <Input {...register('title', { required: true })} />
                    </Label>

                    <div style={{ gridArea: 'description' }}>
                        <Label title={`${L('description')}*`} error={errors?.description?.message} />
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => {
                                return <Quill {...field} placeholder="Skriv något här..." />;
                            }}
                        />
                    </div>
                    <Label
                        style={{ gridArea: 'reply_mail' }}
                        htmlFor="reply_mail"
                        title={`${L('reply_mail')}`}
                        error={L(errors?.reply_mail?.message || '')}
                    >
                        <Input {...register('reply_mail', { required: true })} />
                    </Label>
                    <div
                        style={{
                            placeSelf: 'end',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            gap: style.margin.m,
                            gridArea: 'buttons',
                        }}
                    >
                        <Button
                            type="button"
                            variant="primary"
                            onClick={() => {
                                if (isDirty) {
                                    return triggerDirtyFieldsToats({
                                        continueButtonText: L('continue_without_saving'),
                                        continueFn: () => {
                                            history.push('/news');
                                        },
                                    });
                                }
                                history.push('/news');
                            }}
                            role="link"
                        >
                            {L('cancel')}
                        </Button>
                        <DropdownButton
                            variant="filledPrimary"
                            options={[
                                {
                                    dropdownItem: (
                                        <DropdownButton.DropdownItem>{L('save_to_draft')}</DropdownButton.DropdownItem>
                                    ),
                                    id: 'save_to_draft',
                                    button: (
                                        <DropdownButton.Button
                                            onClick={handleSubmit((formValues) => submit(formValues, 'draft'))}
                                        >
                                            {L('save_to_draft')}
                                        </DropdownButton.Button>
                                    ),
                                },
                                {
                                    dropdownItem: (
                                        <DropdownButton.DropdownItem>
                                            {L('save_and_publish')}
                                        </DropdownButton.DropdownItem>
                                    ),
                                    id: 'save_and_publish',
                                    button: (
                                        <DropdownButton.Button
                                            onClick={handleSubmit((formValues) => submit(formValues, 'publish'))}
                                        >
                                            {L('save_and_publish')}
                                        </DropdownButton.Button>
                                    ),
                                },
                            ]}
                            setOptionId={(state) => {
                                setSubmitButtonState(state);
                            }}
                            optionId={submitButtonState}
                        />
                    </div>
                </GridContainer>
            }
        />
    );
};

export default CreateNewNewsPage;
